import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const useGlass = (overrides = {}) => {
  const { tokens } = useTheme();
  return {
    background: tokens.colors.glass,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: 20,
    boxShadow: tokens.dark ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 40px rgba(0,0,0,0.08)',
    ...overrides,
  };
};

export const useGlassInput = () => {
  const { tokens } = useTheme();
  return {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: 10,
    fontSize: 13,
    background: tokens.colors.inputBg,
    backdropFilter: 'blur(8px)',
    color: tokens.colors.textPrimary,
    outline: 'none',
    transition: 'all 0.15s',
  };
};

export const useGlassButton = (color = 'accent') => {
  const { tokens } = useTheme();
  const c = color === 'accent' ? tokens.colors.accent :
            color === 'success' ? tokens.colors.success :
            color === 'danger' ? tokens.colors.danger :
            color === 'secondary' ? tokens.colors.textSecondary : tokens.colors.accent;
  return {
    padding: '8px 20px',
    border: `1px solid ${c}33`,
    borderRadius: 10,
    background: `${c}14`,
    color: c,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backdropFilter: 'blur(8px)',
    transition: 'all 0.15s',
  };
};

export const ThemeProvider = ({ children }) => {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return false; // Default to light mode
  });

  const toggleTheme = () => setDark(d => !d);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('theme', dark ? 'dark' : 'light');
  }, [dark]);

  const tokens = useMemo(() => ({
    dark,
    colors: {
      // Glass surfaces — LabFlow Liquid Glass (Apple WWDC 2025)
      glass: dark ? 'rgba(30,30,35,0.68)' : 'rgba(255,255,255,0.78)',
      glassStrong: dark ? 'rgba(40,40,45,0.78)' : 'rgba(255,255,255,0.88)',
      glassBg: dark ? 'rgba(22,22,24,0.92)' : 'rgba(255,255,255,0.88)',
      glassCard: dark ? 'rgba(30,30,35,0.68)' : 'rgba(255,255,255,0.78)',
      glassTint: dark ? 'rgba(176,224,230,0.08)' : 'rgba(176,224,230,0.15)',
      glassModal: dark ? 'rgba(22,22,24,0.95)' : 'rgba(255,255,255,0.95)',
      glassOverlay: dark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.3)',

      // Borders
      border: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      borderStrong: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
      borderSubtle: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      divider: dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',

      // Text — LabFlow Apple palette
      textPrimary: dark ? '#F5F5F7' : '#1D1D1F',
      textSecondary: dark ? '#A1A1A6' : '#6E6E73',
      textTertiary: dark ? '#636366' : '#AEAEB2',
      textInverse: dark ? '#1D1D1F' : '#F5F5F7',

      // Semantic — Apple vibrant accents
      accent: dark ? '#0A84FF' : '#007AFF',
      accentBg: dark ? 'rgba(10,132,255,0.12)' : 'rgba(0,122,255,0.08)',
      accentBorder: dark ? 'rgba(10,132,255,0.25)' : 'rgba(0,122,255,0.2)',
      success: dark ? '#30D158' : '#34C759',
      successBg: dark ? 'rgba(48,209,88,0.12)' : 'rgba(52,199,89,0.08)',
      warning: dark ? '#FFD60A' : '#FFCC00',
      warningBg: dark ? 'rgba(255,214,10,0.12)' : 'rgba(255,204,0,0.08)',
      danger: dark ? '#FF453A' : '#FF3B30',
      dangerBg: dark ? 'rgba(255,69,58,0.12)' : 'rgba(255,59,48,0.06)',
      highlightOrange: dark ? '#FF9F0A' : '#FF9500',
      powderBlue: dark ? '#5A9BA8' : '#B0E0E6',
      info: dark ? '#0A84FF' : '#007AFF',
      infoBg: dark ? 'rgba(10,132,255,0.12)' : 'rgba(0,122,255,0.08)',

      // Surfaces — LabFlow Apple compliant
      pageBg: dark ? '#0F0F11' : '#F5F5F7',
      surface: dark ? '#1C1C1E' : '#F5F5F7',
      surfaceElevated: dark ? '#2C2C2E' : '#ffffff',
      cardBg: dark ? 'rgba(30,30,35,0.68)' : 'rgba(255,255,255,0.78)',
      sidebarBg: dark ? 'rgba(22,22,24,0.92)' : 'rgba(245,245,247,0.88)',
      headerBg: dark ? 'rgba(22,22,24,0.88)' : 'rgba(255,255,255,0.78)',

      // Inputs
      inputBg: dark ? 'rgba(44,44,48,0.5)' : 'rgba(255,255,255,0.7)',
      inputBorder: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      inputFocus: dark ? 'rgba(10,132,255,0.3)' : 'rgba(0,122,255,0.3)',

      // Table
      tableHeaderBg: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      tableRowBorder: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      tableRowHover: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',

      // Hover
      hoverBg: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    },
    shadows: {
      soft: dark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.06)',
      medium: dark ? '0 8px 32px -12px rgba(0,0,0,0.6)' : '0 8px 32px -12px rgba(0,0,0,0.15)',
      elevated: dark ? '0 20px 50px -15px rgba(0,0,0,0.7)' : '0 20px 50px -15px rgba(0,0,0,0.25)',
    },
  }), [dark]);

  return (
    <ThemeContext.Provider value={{ tokens, toggleTheme, dark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
